import {AuditAdjustmentHeader} from '@/services/auditService/auditAdjustmentService';
import {AdjustmentMode} from '@/services/auditService/adjustmentModes';
import {useFsAdjustStore} from '@/store/audit';
import userStore from '@/store/admin/userStore';
import {formatDateTime} from '@/utils/dataUtils';
import {CommonRow, DueFromToDirRow} from '@/services/auditService/interface/wp-row';
import {FsCategoryLine} from '@/store/audit/fsCategoryStore';
import {calculateTotalAdjustment, validateAndGetStrategy} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/working-helpers';
import {computeStoredAdjustmentFromUserInput, determineRenderPosition, isCommonRowSyntheticEditCode} from '@/adjustment/adjust/utils/classifyRows';
import {
    DTD_CL_CODE,
    DTD_DEFAULT_ORIGINAL_ENTRY,
    computeAppendBalanceAdjustmentForDtd,
    getOriginalEntryFromParent,
    isDtdClFsCode,
    isTaxBfSpecialDtdCode,
    usesDtdAdjustmentPathScoped,
    resolveDtdAppendEffectiveEntry,
    resolveDtdEntryFromUserSignedAdjustment,
    specialSignedAdjust,
    sumGenericDetailAdjustments,
    resolveDtdSignedAdjustmentFromBackend,
    convertDtdClUserInput,
    resolveDtdToPaperDetailRowAdjustment,
    toPaperSpecialPairing,
    rowBelongsToDtdFromPaper,
} from '@/adjustment/adjust/utils/specialAdjustFlow';
import {useFsCategoryStore} from '@/store/audit/fsCategoryStore';
import {buildDetailsPayload} from '@/adjustment/adapters';
import {getBackendAdjustmentTotal} from '@/adjustment/adjust/utils/adjustmentLockUtils';
import {ShowSnackbarFn} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/working-helpers';
import type {AuditAdjustLine} from '@/services/auditService/interface/adjust-orchestrate';

/** resolveLineFsId 只需从 line 上读取 fsId，兼容 FsCategoryLine / FsSimpleData 等 */
type LineFsIdSource = { fsId?: number } | null | undefined;

/** 带符号 adjustment 的 line（fromPaper 组装时保留用户输入符号） */
export type AdjustLineWithSignedAdjustment = AuditAdjustLine & {
    adjustment?: number;
};

/**
 * Exclusive FS codes: submenu-only in WP_TO_WP; also FS_TO_FS opening-edit filter.
 */
export const EXCLUSIVE_CODES = new Set([
    // Property, plant and equipment
    'BS-NCA-PPE',
    // Intangible assets
    'BS-NCA-IA',
    // Investment in taxi cabs and licenses
    'BS-NCA-ITL',
    // Other non-current assets
    'BS-NCA-ONA',
    // Bank loan, secured (current & non-current)
    'BS-CL-BLS',
    'BS-NCL-BLS',
    // Obligation under finance lease (current & non-current)
    'BS-CL-OFL',
    'BS-NCL-OFL',
    // Issued Capital
    'BS-EQ-IC',
    // Depreciation / Amortization
    'PL-AE-DEP',
    'PL-AE-AIA',
    // Rental expense
    'PL-AE-REN',
    // Loan interest expenses
    'PL-FC-LI',
]);

/**
 * 检查节点的 code 是否在 EXCLUSIVE_CODES 中
 * @param code - 节点的 code
 * @returns 是否为 exclusive code
 */
export function isExclusiveCode(code: string | null | undefined): boolean {
    if (!code) return false;
    return EXCLUSIVE_CODES.has(code.trim().toUpperCase());
}

/**
 * AdjustTabsDialog 中不需要拉取/组装 working-paper details 的 code。
 * 这些节点仅提交 adjustmentAmount + entry，details 保持为空。
 * Bank/Vehicle loan 字段 WP→WP 需携带 bankLoans/vehicleLoans + adjustment，不在此列。
 * PL-AE-REN：默认无 WP detail；field WP→WP（rows+adjustment）由 RentalAdjustPanel 显式带入，
 * 有 details 时提交路径不会走空 details 强制清空（见 buildSubmitHeader）。
 */
export const NO_WP_DETAIL_CODES = new Set([
    'PL-AE-REN',
    // 'BS-NCA-PPE',
    'BS-NCA-IA',
    'BS-NCA-ITL',
    // CommonInvestment NBV C/F schedule adj：与 IA 同 line-only（details: []）
    'BS-NCA-ISUB',
    'BS-NCA-IASS',
    'BS-CA-IOT',
    'BS-EQ-IC',
    'PL-AE-AIA',
]);

export function isNoWpDetailCode(code: string | null | undefined): boolean {
    if (!code) return false;
    return NO_WP_DETAIL_CODES.has(code.trim().toUpperCase());
}

/**
 * FsSelectionDrawer 中默认隐藏的 FS code。
 * PL-AE-MPF 在 WP_TO_WP 下由 Drawer 放开为二级菜单（见 FsSelectionDrawer）。
 */
export const ALWAYS_HIDDEN_FS_SELECTION_CODES = new Set([
    'PL-AE-MPF',
]);

/**
 * 互斥组：fromPaper 为组内任一 code 时，Drawer 中禁止选择同组其余 code 作为 toPaper。
 * 自定义节点 code 不在组内；若 templateCodeRef 指向组内模板 code，则继承该组互斥规则。
 */
export const ADJUSTMENT_CROSS_EXCLUSIVE_GROUPS: ReadonlyArray<readonly string[]> = [
    ['BS-CA-DIR', 'BS-CL-DTD'],
    ['BS-NCA-PPE', 'PL-AE-DEP'],
    ['PL-OI-GOD', 'PL-AE-LOD'],
    ['PL-OI-EXG', 'PL-AE-EXL'],
    ['BS-CA-INV', 'PL-COT-OI', 'PL-COT-CI'],
    ['BS-NCA-IA', 'PL-AE-AIA'],
    ['BS-CL-BLS', 'BS-NCL-BLS', 'PL-FC-LI'],
    ['BS-CL-OFL', 'BS-NCL-OFL', 'PL-FC-LI'],
];

function normalizeAdjustCode(code: string | null | undefined): string {
    return String(code || '').trim().toUpperCase();
}

/** 查找 code 所属的互斥组；未命中时返回 null */
export function findAdjustmentCrossExclusiveGroup(code: string | null | undefined): readonly string[] | null {
    const normalized = normalizeAdjustCode(code);
    if (!normalized) return null;
    return ADJUSTMENT_CROSS_EXCLUSIVE_GROUPS.find(group => group.includes(normalized)) ?? null;
}

/**
 * 解析节点在互斥规则中使用的有效 code。
 * 自定义节点若 templateCodeRef 指向组内模板，则按 templateCodeRef 参与互斥。
 */
export function resolveEffectiveExclusiveCode(
    code: string | null | undefined,
    templateCodeRef?: string | null,
): string {
    const normalizedRef = normalizeAdjustCode(templateCodeRef);
    if (normalizedRef && findAdjustmentCrossExclusiveGroup(normalizedRef)) {
        return normalizedRef;
    }
    return normalizeAdjustCode(code);
}

/** 节点是否应被 FsSelectionDrawer 全局隐藏 */
export function isAlwaysHiddenFsSelectionCode(
    code: string | null | undefined,
    templateCodeRef?: string | null,
): boolean {
    const normalizedCode = normalizeAdjustCode(code);
    const normalizedRef = normalizeAdjustCode(templateCodeRef);
    return ALWAYS_HIDDEN_FS_SELECTION_CODES.has(normalizedCode)
        || ALWAYS_HIDDEN_FS_SELECTION_CODES.has(normalizedRef);
}

/**
 * fromPaper 发起 adjustment 时，目标节点是否与 fromPaper 处于同一互斥组的对侧。
 */
export function isCrossExclusiveAdjustmentTarget(
    fromCode: string | null | undefined,
    target: { code?: string | null; templateCodeRef?: string | null },
): boolean {
    const fromEffective = resolveEffectiveExclusiveCode(fromCode);
    const fromGroup = findAdjustmentCrossExclusiveGroup(fromEffective);
    if (!fromGroup) return false;

    const targetEffective = resolveEffectiveExclusiveCode(target.code, target.templateCodeRef);
    if (!targetEffective || targetEffective === fromEffective) return false;

    return fromGroup.includes(targetEffective);
}

/**
 * 合并数据参数
 */
export interface MergeDataParams {
    categoryLine: FsCategoryLine | null;
    payloadLine?: FsCategoryLine | null;
    showSnackbar: ShowSnackbarFn;
    commonRow: CommonRow[];
    statement: 'BS' | 'PL';
    companyId: number;
    annualId: number;
    totalsFinal: number;
    currency: string;
    amountDifference: number;
    entry?: string | null;
}

/**
 * 统一合并数据参数 - 支持所有 Working Paper 类型
 * 通过 detailsData 透传 strategies-helper 支持的所有字段
 */
export interface UnifiedMergeDataParams {
    // 基础参数
    categoryLine: FsCategoryLine | null;
    payloadLine?: FsCategoryLine | null;
    showSnackbar: ShowSnackbarFn;
    statement: 'BS' | 'PL';
    companyId: number;
    annualId: number;
    currency: string;
    entry?: string | null;

    // 计算值
    totalsFinal: number;
    amountDifference: number;

    // 数据参数 - 动态 key（如 commonRow, bankRows 等），根据组件类型变化
    detailsData: Record<string, any>;

    // 需要生成负 ID 的行数组字段名列表（可选，默认处理常见字段）
    negativeIdFields?: string[];
}

/**
 * WP_TO_WP 模式返回结构
 * 只包含 line 信息，用于 WP 到 WP 的调整
 */
export interface Wp2WpResult {
    line: AdjustLineWithSignedAdjustment;
}


/**
 * 统一合并数据返回结构
 * line 和 details 分离，line 结构与 AuditAdjustLine 一致
 */
export interface UnifiedMergeDataResult {
    companyId: number;
    annualId: number;
    code: string;
    adjustment: number;
    entry: string | null;
    // line 结构与 AuditAdjustLine 一致（不包含 details）
    line: AdjustLineWithSignedAdjustment & {
        statement?: 'BS' | 'PL';
        scheduleName?: string;
        openingAmount?: number;
        closingAmount?: number;
    };
    // details 分离，包含带负增长 ID 的行数据
    details: Record<string, any>;
    // adjustmentLine: 向后兼容 FromPaperData，line + details 合并
    adjustmentLine: AdjustLineWithSignedAdjustment & {
        statement?: 'BS' | 'PL';
        scheduleName?: string;
        openingAmount?: number;
        closingAmount?: number;
    };
}

/**
 * 默认需要生成负 ID 的行数组字段名
 */
const DEFAULT_NEGATIVE_ID_FIELDS = [
    'rows',
    'commonRow',
    'testRow',
    'testRows',
    'relatedRows',
    'bankRows',
    'bankRow',
    'debtRows',
    'debtFeeRow',
    'feesRows',
    'principalSubsidiaryRows',
    'costRows',
    'impairmentLossRows',
    'dueFromToDirectorsRows',
    'dueFromToDirRow',
    'gainExchange',
    'lossExchange',
    'gainExchangeRows',
    'lossExchangeRows',
    'gainDisposal',
    'lossDisposal',
    'gainDisposalRows',
    'lossDisposalRows',
    'commissionPaymentRows',
    'whtRows',
    'taxDeductibleRows',
    'nonTaxDeductibleRows',
    'locationOfPropertyRow',
    'commercialAllowanceRow',
    'industrialAllowanceRow',
    'furnitureFixtureRow',
    'computerHardSoftRow',
    'fullPayMotorRow',
    'hirePurchaseMotorRow',
    'machineryRow',
    'liftEscalatorRow',
    'equipmentRow',
    'intangibleRows',
    'staffRow',
    'workerRow',
    'directorRow',
    'directorQuarterRow',
    'salary',
    'directorSalary',
    'quarter',
    'bankLoans',
    'vehicleLoans',
    'bankInterestIncome',
    'otherInterestIncome',
    'salesIncome',
    'purchases',
    'rentalRows',
    'subsidiaryRow',
    'associateRow',
    'intangibleRow',
    'taxiLicenseDisRow',
    'fullPayTaxiCabDisRow',
    'hirePurchaseTaxiCabDisRow',
    'trustsRow',
];

/**
 * 为所有行数组生成占位负 ID（仅针对没有正数 ID 的新行）
 * 保留已有的正数 ID（来自后端的数据）
 *
 * WHY: 后端 populateRowIdsToAttrs 将负 rowId 解释为数组下标：-(index + 1) → savedIds[index]。
 * 旧实现按「仅新行」紧凑计数（首个新行恒为 -1），Add Row 落在 index≥1 时会把 adj 绑到首行。
 *
 * @param data - 包含行数组的数据对象
 * @param fields - 需要处理的字段名列表
 * @returns 处理后的数据对象
 */
function generateNegativeIdsForAllRows(data: Record<string, any>, fields: string[] = DEFAULT_NEGATIVE_ID_FIELDS
): Record<string, any> {
    const result: Record<string, any> = {};

    for (const [key, value] of Object.entries(data)) {
        if (fields.includes(key) && Array.isArray(value)) {
            const isBankRow = key === 'bankRow' || key === 'bankRows';
            // 按数组下标生成占位 ID，与后端 -(index + 1) 契约对齐
            result[key] = value.map((row: any, index: number) => {
                const existingId = row?.id;
                const bankId = isBankRow ? row?.bankId : undefined;
                const hasValidBackendId =
                    (typeof existingId === 'number' && existingId > 0)
                    || (typeof bankId === 'number' && bankId > 0);

                if (hasValidBackendId) {
                    return row;
                }

                const indexId = -(index + 1);
                const next: Record<string, any> = {
                    ...row,
                    id: indexId,
                };
                // bankRow：id 与 bankId 同步，供 resolveBankRowId / extractPriorBankAttrRowIds 一致命中
                if (isBankRow && (bankId == null || (typeof bankId === 'number' && bankId <= 0))) {
                    next.bankId = indexId;
                }
                return next;
            });
        } else {
            // 非数组字段或不在处理列表中的字段，直接复制
            result[key] = value;
        }
    }

    return result;
}

/**
 * 移除所有行数组中的 adjustments 和 mpfAdjustments 字段
 * 这些字段用于前端渲染展示，不应发送到后端 API
 * @param data - 包含行数组的数据对象
 * @param fields - 需要处理的字段名列表
 * @returns 处理后的数据对象（移除了 adjustments/mpfAdjustments）
 */
function removeAdjustmentsFromRows(data: Record<string, any>, fields: string[] = DEFAULT_NEGATIVE_ID_FIELDS
): Record<string, any> {
    const result: Record<string, any> = {};

    for (const [key, value] of Object.entries(data)) {
        if (fields.includes(key) && Array.isArray(value)) {
            // 移除每行中的 adjustments 和 mpfAdjustments 字段
            result[key] = value.map((row: any) => {
                const {adjustments, mpfAdjustments, ...rest} = row;
                return rest;
            });
        } else {
            // 非数组字段或不在处理列表中的字段，直接复制
            result[key] = value;
        }
    }

    return result;
}

/**
 * BS-CA-DIR / BS-CL-DTD（Due From/To Directors）节点
 */
export function isDueToDirectorsCode(code?: string): boolean {
    const normalized = String(code || '').trim().toUpperCase();
    return normalized === 'BS-CA-DIR' || normalized === 'BS-CL-DTD';
}

export {
    DTD_CL_CODE,
    isDtdClFsCode,
    usesDtdAdjustmentPath,
    usesDtdAdjustmentPathScoped,
    usesSpecialAdjustPath,
    usesSpecialAdjustPathScoped,
    isSpecialAdjustScopedEligible,
    isTaxRefundableBfDtdCode,
    isTaxPayableBfDtdCode,
    isTaxBfSpecialDtdCode,
    applyTaxPayableBfMergeLine,
} from '@/adjustment/adjust/utils/specialAdjustFlow';
export {
    flipDtdDetailRowAdjustment,
    flipDtdDetailsAdjustment,
    resolveDtdLineFieldsAfterFlip,
    resolveDtdUserSignedRowAdjustment,
    resolveDtdClAdjustmentForPosition,
    resolveDueFromToDirectorsFromEntry,
    resolveDtdSignedAdjustmentFromBackend,
    toPaperSpecialPairing,
    computeAppendBalanceAdjustmentForDtd,
    applyCrMergeLine,
    applyDrMergeLine,
    getOriginalEntryFromParent,
    TAX_REFUNDABLE_BF_CODE,
    TAX_PAYABLE_BF_CODE,
} from '@/adjustment/adjust/utils/specialAdjustFlow';

export const DIR_CA_CODE = 'BS-CA-DIR';

/** Sales Income (PL-TURN-SI) / Service Income (PL-TURN-SE)，共用 SalesIncomeWp，走非 DTD 调整路径 */
export const TURNOVER_INCOME_FS_CODES = new Set(['PL-TURN-SI', 'PL-TURN-SE']);

/** Turnover 子节点 save 后触发 PL-TURN draft 刷新（含 PL-TURN-RI） */
export const TURNOVER_ROLLUP_FS_CODES = new Set(['PL-TURN-SI', 'PL-TURN-SE', 'PL-TURN-RI']);

export function isTurnoverIncomeFsCode(code?: string | null): boolean {
    return TURNOVER_INCOME_FS_CODES.has(String(code || '').trim().toUpperCase());
}

export function isTurnoverRollupFsCode(code?: string | null): boolean {
    return TURNOVER_ROLLUP_FS_CODES.has(String(code || '').trim().toUpperCase());
}

/**
 * 从候选 detail 中提取已有 annualInfo（不发明默认值，避免误写 account）。
 * 供 WP_TO_WP 提交时给缺失 annualInfo 的 PL-TURN-SI/SE detail 回填。
 */
export function pickExistingTurnoverAnnualInfo(
    candidates: Array<any | null | undefined>,
    annualId?: number | null,
): Record<string, any> | undefined {
    for (const candidate of candidates) {
        if (!candidate || typeof candidate !== 'object') continue;
        const annualInfo = (candidate as any).annualInfo;
        if (!annualInfo || typeof annualInfo !== 'object' || Array.isArray(annualInfo)) continue;
        const next = {...annualInfo};
        if (next.annualId == null && annualId != null) {
            next.annualId = annualId;
        }
        return next;
    }
    return undefined;
}

/**
 * 仅当 detail 缺少 annualInfo 且候选中已有时回填；已有则原样返回。
 */
export function withTurnoverAnnualInfoIfMissing(
    detail: any,
    candidates: Array<any | null | undefined>,
    annualId?: number | null,
): any {
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) {
        return detail;
    }
    if ((detail as any).annualInfo != null) {
        return detail;
    }
    const annualInfo = pickExistingTurnoverAnnualInfo(candidates, annualId);
    if (!annualInfo) {
        return detail;
    }
    return {...detail, annualInfo};
}

export function unwrapDueFromToDirDetail(details: any): any {
    if (!details) return null;
    if (Array.isArray(details)) return details[0] ?? null;
    return details;
}

const sumRowAdjustments = (list: any[]): number =>
    list.reduce((sum, r) => sum + (Number(r.adjustment) || 0), 0);

/**
 * 判断 dueFromToDirRow 单行是否归属当前 fromPaper（DIR/DTD）。
 * 仅用于 BS-CA-DIR / BS-CL-DTD 节点；其他 code 恒为 true。
 * Dr/Cr classify / sumDetail 等沿用此口径，勿改为余额分区。
 */
export function rowBelongsToDueFromToDirFromPaper(
    fromFsCode: string,
    row: { perClient?: number; code?: string; adjustment?: number },
): boolean {
    const code = String(fromFsCode || '').trim().toUpperCase();
    if (!isDueToDirectorsCode(code)) return true;

    const perClient = Number(row?.perClient) || 0;
    const rowCode = String(row?.code || '').trim().toUpperCase();
    const rowAdj = Math.abs(Number(row?.adjustment) || 0);

    if (code === DTD_CL_CODE) {
        return rowBelongsToDtdFromPaper(row);
    }
    if (code === DIR_CA_CODE) {
        if (rowCode === DIR_CA_CODE) return true;
        if (perClient < 0) return false;
        if (rowCode === DTD_CL_CODE) return false;
        return perClient >= 0 || rowAdj >= 0.001;
    }
    return true;
}

/**
 * perClient + 已保存 adjustments（不含当前输入的 singular adjustment）。
 * 与 DueFromToDirectorsWp 侧别口径一致；仅供 toPaper 选行使用。
 */
export function getDueFromToDirBalanceBasis(row: {
    perClient?: number | null;
    adjustments?: any[];
} & Record<string, any>): number {
    return (Number(row?.perClient) || 0) + getBackendAdjustmentTotal(row);
}

/**
 * toPaper 选 DIR/DTD 时的行归属：按余额符号分区，忽略 row.code。
 * 共用底稿接口常把两行都标成 BS-CA-DIR，不能据此归属。
 * 勿用于 classifyRows / sumDueFromToDirDetailAdjustment。
 */
export function rowBelongsToDueFromToDirByBalance(
    fsCode: string,
    row: { perClient?: number | null; adjustments?: any[] } & Record<string, any>,
): boolean {
    const code = String(fsCode || '').trim().toUpperCase();
    if (!isDueToDirectorsCode(code)) return true;
    const basis = getDueFromToDirBalanceBasis(row);
    if (code === DIR_CA_CODE) return basis >= 0;
    if (code === DTD_CL_CODE) return basis < 0;
    return true;
}

export function tagDueFromToDirRowsWithFsCode(rows: DueFromToDirRow[], fsCode: string): DueFromToDirRow[] {
    const code = String(fsCode || '').trim().toUpperCase();
    if (!isDueToDirectorsCode(code) || !Array.isArray(rows)) return rows;
    return rows.map((row) => {
        const adj = Math.abs(Number(row?.adjustment) || 0);
        if (adj < 0.001) return row;
        return {...row, code};
    });
}

/**
 * 将后端/legacy 行数据规范为 wp-row.DueFromToDirRow 结构。
 * 映射 item → directorName，并剔除 CommonRow 等无关字段。
 */
export function normalizeDueFromToDirRow(row: any): DueFromToDirRow {
    if (!row || typeof row !== 'object') return {};

    const result: DueFromToDirRow = {};

    if (row.id != null) {
        const id = Number(row.id);
        if (Number.isFinite(id)) result.id = id;
    }
    if (row.annualId != null) {
        const annualId = Number(row.annualId);
        if (Number.isFinite(annualId)) result.annualId = annualId;
    }
    if (row.code != null && String(row.code).trim()) {
        result.code = String(row.code).trim().toUpperCase();
    }
    if (row.directorId != null) {
        const directorId = Number(row.directorId);
        if (Number.isFinite(directorId) && directorId > 0) result.directorId = directorId;
    }
    const directorName = String(row.directorName ?? row.item ?? '').trim();
    if (directorName) result.directorName = directorName;
    if (row.resignDate !== undefined) result.resignDate = row.resignDate;
    if (row.perClient != null) result.perClient = Number(row.perClient) || 0;
    if (row.adjustment != null) result.adjustment = Number(row.adjustment) || 0;
    if (Array.isArray(row.adjustments) && row.adjustments.length > 0) {
        result.adjustments = row.adjustments;
    }
    if (row.lastYearFinal != null) result.lastYearFinal = Number(row.lastYearFinal) || 0;
    if (row.finalChange != null) result.finalChange = Number(row.finalChange);

    return result;
}

/**
 * 将 WP detail 规范为 { dueFromToDirRow: DueFromToDirRow[] }，保留元数据字段。
 * 支持 legacy dueFromToDirectors 数组。
 */
export function normalizeDueFromToDirDetail(detail: any): Record<string, any> {
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) {
        return {dueFromToDirRow: []};
    }

    const result: Record<string, any> = {};
    for (const [key, value] of Object.entries(detail)) {
        if (key === 'dueFromToDirRow' || key === 'dueFromToDirectors') continue;
        if (!Array.isArray(value)) {
            result[key] = value;
        }
    }

    let rawRows: any[] = [];
    if (Array.isArray(detail.dueFromToDirRow) && detail.dueFromToDirRow.length > 0) {
        rawRows = detail.dueFromToDirRow;
    } else if (Array.isArray(detail.dueFromToDirectors) && detail.dueFromToDirectors.length > 0) {
        rawRows = detail.dueFromToDirectors;
    }

    result.dueFromToDirRow = rawRows.map(normalizeDueFromToDirRow);
    return result;
}

/** WP 初始化行：仅有 directorId / directorName，尚未按 FS 节点绑定 code */
export function isDueFromToDirDirectorRow(row: any): boolean {
    if (!row || typeof row !== 'object') return false;
    const directorId = Number(row.directorId);
    if (Number.isFinite(directorId) && directorId > 0) return true;
    return Boolean(String(row.directorName ?? row.item ?? '').trim());
}

export function hasValidDueFromToDirDirectorId(row: any): boolean {
    const directorId = Number(row?.directorId);
    return Number.isFinite(directorId) && directorId > 0;
}

/**
 * 判断 dueFromToDirRow 是否全部缺少有效 directorId。
 * 无行或仅有 directorName 而无 directorId 时返回 true。
 */
export function allDueFromToDirRowsMissingDirectorId(rows: any[]): boolean {
    if (!Array.isArray(rows) || rows.length === 0) return true;
    const directorRows = rows.filter(isDueFromToDirDirectorRow);
    if (directorRows.length === 0) return true;
    return directorRows.every((row) => !hasValidDueFromToDirDirectorId(row));
}

/** toPaper DIR/DTD 需先补全 Issued Capital 董事信息 */
export function dueFromToDirDetailRequiresIssuedCapital(detail: any): boolean {
    const normalized = normalizeDueFromToDirDetail(detail);
    const rows: any[] = normalized.dueFromToDirRow ?? [];
    return allDueFromToDirRowsMissingDirectorId(rows);
}

/**
 * 为 toPaper 筛选 dueFromToDirRow。
 * 按 perClient+adjustments 符号归属；已有非零余额时不回退到对侧董事。
 * 仅初始化（尚无 signed balance）时回退全部董事行供绑定 toPaper.code。
 */
export function filterDueFromToDirRowsForToPaper(rows: any[], toPaperCode: string): any[] {
    if (!Array.isArray(rows)) return [];
    const fsCode = String(toPaperCode || '').trim().toUpperCase();
    if (!isDueToDirectorsCode(fsCode)) return rows;

    const directorRows = rows.filter(isDueFromToDirDirectorRow);
    const byBelongs = directorRows.filter((r) => rowBelongsToDueFromToDirByBalance(fsCode, r));
    if (byBelongs.length > 0) return byBelongs;

    const hasSignedBalance = directorRows.some(
        (r) => Math.abs(getDueFromToDirBalanceBasis(r)) >= 0.001,
    );
    if (hasSignedBalance) return byBelongs;
    return directorRows;
}

export interface DueFromToDirToPaperOptions {
    targetRowIndex?: number;
    perClient?: number;
}

/**
 * 将 DIR 底稿中的董事行绑定到 toPaper FS 节点（BS-CA-DIR / BS-CL-DTD），
 * 保留 directorId/directorName，并按 toPaper.code 写入 dueFromToDirRow[].code。
 *
 * 余额符号过滤为空时：若带非零 adjustment（其它组件→DIR/DTD 重分类），仍绑定董事行，
 * 避免 detail.adjustment 丢失导致 line.adjustmentAmount=0。
 */
export function buildDueFromToDirDetailForToPaper(
    sourceDetail: any,
    toPaperCode: string,
    adjustmentAmount: number,
    options?: DueFromToDirToPaperOptions,
): any {
    const fsCode = String(toPaperCode || '').trim().toUpperCase();
    if (!isDueToDirectorsCode(fsCode)) return sourceDetail;

    const base = sourceDetail && typeof sourceDetail === 'object' && !Array.isArray(sourceDetail)
        ? normalizeDueFromToDirDetail(sourceDetail)
        : {dueFromToDirRow: []};
    const allRows: DueFromToDirRow[] = Array.isArray(base.dueFromToDirRow) ? [...base.dueFromToDirRow] : [];
    if (allRows.length === 0) return base;

    const pool = filterDueFromToDirRowsForToPaper(allRows, fsCode);
    const hasSignedAdj = Math.abs(Number(adjustmentAmount) || 0) >= 0.001;

    let resolvedIndex = options?.targetRowIndex;
    if (resolvedIndex == null || resolvedIndex < 0 || resolvedIndex >= allRows.length) {
        if (pool.length > 0) {
            resolvedIndex = allRows.indexOf(pool[0]);
        } else if (hasSignedAdj) {
            // 对侧余额 / 仅 DTD 行时余额过滤为空：仍需落 adjustment（重分类）
            const directorRows = allRows.filter(isDueFromToDirDirectorRow);
            if (directorRows.length === 0) return base;
            const byCode = directorRows.find(
                (r) => String(r?.code || '').trim().toUpperCase() === fsCode,
            );
            resolvedIndex = allRows.indexOf(byCode ?? directorRows[0]);
        } else {
            return base;
        }
    }
    if (resolvedIndex < 0) resolvedIndex = 0;

    const targetSource = allRows[resolvedIndex] ?? pool[0] ?? allRows[0];
    const defaultPerClient = Number(targetSource?.perClient) || 0;
    const normalizePerClientForFs = (value: number): number => {
        if (fsCode === DTD_CL_CODE && value > 0) return -Math.abs(value);
        return value;
    };
    const resolvedPerClient = options?.perClient !== undefined
        ? normalizePerClientForFs(options.perClient)
        : normalizePerClientForFs(defaultPerClient);

    const nextRows = allRows.map((row, idx) => {
        const {adjustments: _adj, mpfAdjustments: _mpfAdj, ...rowRest} = row as any;
        if (idx === resolvedIndex) {
            const lineSignedAdj = Number(adjustmentAmount) || 0;
            const rowAdjustment = fsCode === DTD_CL_CODE
                ? resolveDtdToPaperDetailRowAdjustment(lineSignedAdj)
                : lineSignedAdj;
            return normalizeDueFromToDirRow({
                ...rowRest,
                code: fsCode,
                directorId: row.directorId,
                directorName: row.directorName,
                perClient: resolvedPerClient,
                adjustment: rowAdjustment,
            });
        }
        return normalizeDueFromToDirRow({...rowRest, adjustment: 0});
    });

    return {...base, dueFromToDirRow: tagDueFromToDirRowsWithFsCode(nextRows, fsCode)};
}

/**
 * 按 FS 节点过滤 dueFromToDirRow 并汇总带符号 adjustment。
 * 优先 perClient 分区；DTD/DIR 初始化无对应 perClient 时回退 row.code 或非零 adjustment 行。
 */
export function sumDueFromToDirDetailAdjustment(detail: any, fsCode?: string): number {
    const normalized = unwrapDueFromToDirDetail(detail);
    if (!normalized || typeof normalized !== 'object') return 0;
    const rows: any[] = Array.isArray(normalized.dueFromToDirRow) ? normalized.dueFromToDirRow : [];
    const code = String(fsCode || '').trim().toUpperCase();

    if (code === DTD_CL_CODE) {
        const byPerClient = rows.filter((r) => (Number(r.perClient) || 0) < 0);
        const s1 = sumRowAdjustments(byPerClient);
        if (Math.abs(s1) >= 0.001) return s1;

        const byCode = rows.filter((r) => String(r.code || '').trim().toUpperCase() === DTD_CL_CODE);
        const s2 = sumRowAdjustments(byCode);
        if (Math.abs(s2) >= 0.001) return s2;

        const withAdj = rows.filter((r) => rowBelongsToDueFromToDirFromPaper(code, r));
        if (withAdj.length >= 1) return sumRowAdjustments(withAdj);
        return 0;
    }

    if (code === DIR_CA_CODE) {
        const byPerClient = rows.filter((r) => (Number(r.perClient) || 0) >= 0);
        const s1 = sumRowAdjustments(byPerClient);
        if (Math.abs(s1) >= 0.001) return s1;

        const byCode = rows.filter((r) => String(r.code || '').trim().toUpperCase() === DIR_CA_CODE);
        const s2 = sumRowAdjustments(byCode);
        if (Math.abs(s2) >= 0.001) return s2;

        const withAdj = rows.filter((r) => rowBelongsToDueFromToDirFromPaper(code, r));
        if (withAdj.length >= 1) return sumRowAdjustments(withAdj);
        return 0;
    }

    return sumRowAdjustments(rows);
}

/**
 * 解析 DIR/DTD fromPaper 用于 Dr/Cr 列与 toPaper 的有效带符号 adjustment。
 * 规则以「当前用户输入值」为准（非 DTD flip 后的 details 存储值）。
 *
 * BS-CL-DTD：line.adjustment 为用户输入；details 行内为 flip 后存储，需取反还原。
 * BS-CA-DIR：details 与 line 同号，优先汇总 details。
 */
export function resolveDueFromToDirectorsSignedAdjustment(
    fsCode: string,
    detail?: any,
    lineAdjustment?: number | null,
): number {
    const code = String(fsCode || '').trim().toUpperCase();
    const lineAdj = lineAdjustment !== undefined && lineAdjustment !== null
        ? Number(lineAdjustment) || 0
        : null;

    if (code === DTD_CL_CODE) {
        return specialSignedAdjust(detail, lineAdjustment, sumDueFromToDirDetailAdjustment);
    }

    if (detail) {
        const fromDetails = sumDueFromToDirDetailAdjustment(detail, code);
        if (Math.abs(fromDetails) >= 0.001) return fromDetails;
    }
    if (lineAdj !== null) return lineAdj;
    return 0;
}

/**
 * AdjustTabsDialog 行 entry。
 *
 * - specialAdjustFlow（`usesSpecialAdjustPathScoped`：全局 SPECIAL_ADJUST_GLOBAL_CODES，或已注册的 scoped code）：
 *   始终用 DTD 符号规则（含用户负号切列）。勿用 FsCategory `determineRenderPosition`
 *  （Cr 节点下两者语义相反）。
 * - 其余节点：保持 FsCategory 常规规则，不受 specialAdjustFlow 影响。
 */
export function resolveAdjustLineEntry(
    code: string,
    adjustment: number,
    _userEdited = false,
    originalEntryOverride?: DrCrEntry | string | null,
    specialDtdPathCodes?: ReadonlySet<string>,
): DrCrEntry {
    if (usesDtdAdjustmentPathScoped(code, specialDtdPathCodes)) {
        const originalEntry = coerceDrCrEntry(originalEntryOverride)
            ?? getOriginalEntryFromParent(code);
        return resolveDtdEntryFromUserSignedAdjustment(adjustment, originalEntry);
    }
    const originalEntry = coerceDrCrEntry(originalEntryOverride) ?? getOriginalEntryFromFsCategory(code);
    return determineRenderPosition(originalEntry, adjustment);
}

export type DrCrEntry = 'Dr' | 'Cr';

/**
 * 将后端 line.entry + adjustmentAmount 还原为带符号 line 级 adjustment（渲染/分列用）。
 * - DTD：专用符号规则
 * - 非 DTD：entry 表示渲染列，adjustmentAmount 为绝对值（忽略 details 内 adjustments 符号）
 */
export function resolveSignedAdjustmentFromBackendLine(
    entry: string | null | undefined,
    adjustmentAmount: number,
    code?: string | null,
    specialDtdPathCodes?: ReadonlySet<string>,
): number {
    const absAmount = Math.abs(Number(adjustmentAmount) || 0);
    if (absAmount < 0.001) return 0;
    if (usesDtdAdjustmentPathScoped(code, specialDtdPathCodes)) {
        const originalEntry = getOriginalEntryFromParent(String(code || ''));
        return resolveDtdSignedAdjustmentFromBackend(entry, absAmount, originalEntry);
    }
    return entry === 'Cr' ? -absAmount : absAmount;
}

/**
 * 非 DTD：按 FS originalEntry + 渲染列（line.entry）还原带符号 adjustment。
 * 与 determineRenderPosition / computeStoredAdjustmentFromUserInput 一致：
 * 同侧为正、异侧为负。用于 loan WP / commonRow 等 detail.adjustment 回写，
 * 避免 Cr 性质节点被 resolveSignedAdjustmentFromBackendLine（Cr→负）签成错误符号后动态分到 Dr。
 */
export function resolveNonDtdSignedAdjustmentFromRenderEntry(
    renderEntry: string | null | undefined,
    adjustmentAmount: number,
    code?: string | null,
    originalEntryOverride?: string | null,
): number {
    const absAmount = Math.abs(Number(adjustmentAmount) || 0);
    if (absAmount < 0.001) return 0;
    const column: DrCrEntry = renderEntry === 'Cr' ? 'Cr' : 'Dr';
    const originalEntry = coerceDrCrEntry(originalEntryOverride)
        ?? resolveFsNodeOriginalEntry(String(code || ''), originalEntryOverride)
        ?? getOriginalEntryFromFsCategory(String(code || ''));
    const sameSide = (originalEntry === 'Dr') === (column === 'Dr');
    return sameSide ? absAmount : -absAmount;
}

/** 将 string / null 规范化为 Dr/Cr，无法识别时返回 null */
export function coerceDrCrEntry(entry?: string | null): DrCrEntry | null {
    if (entry === 'Dr' || entry === 'Cr') return entry;
    const upper = entry?.toUpperCase?.();
    if (upper?.startsWith('D')) return 'Dr';
    if (upper?.startsWith('C')) return 'Cr';
    return null;
}

/**
 * 从 FsCategoryStore 读取 FS 节点的 originalEntry（借贷原始方向）。
 * Category 未就绪时回退 FsAdjustStore，避免负债节点（如 BS-CL-DTS）误用 fallback Dr。
 */
export function getOriginalEntryFromFsCategory(code: string, fallback: DrCrEntry = 'Dr'): DrCrEntry {
    const normalized = String(code || '').trim().toUpperCase();
    if (!normalized) return fallback;
    const line = useFsCategoryStore.getState().getLineByCode(normalized);
    const entry = line?.entry;
    if (entry?.toUpperCase?.().startsWith('C')) return 'Cr';
    if (entry?.toUpperCase?.().startsWith('D')) return 'Dr';
    const adjustLine = useFsAdjustStore.getState().getLineByCode(normalized);
    const adjustEntry = adjustLine?.entry;
    if (adjustEntry?.toUpperCase?.().startsWith('C')) return 'Cr';
    if (adjustEntry?.toUpperCase?.().startsWith('D')) return 'Dr';
    return fallback;
}

/**
 * 根据 FS 节点 originalEntry 与 adjustment 符号重新判定有效 entry。
 *
 * - originalEntry = Dr：adjustment >= 0 → Dr；adjustment < 0 → Cr
 * - originalEntry = Cr：adjustment >= 0 → Cr；adjustment < 0 → Dr
 */
export function resolveEntryFromFsCategory(code: string, adjustment: number): DrCrEntry {
    const originalEntry = getOriginalEntryFromFsCategory(code);
    return determineRenderPosition(originalEntry, adjustment);
}

/**
 * Append Another Record：计算新 toPaper 带符号 adjustment，
 * 使 determineRenderPosition(originalEntry, result) === fillSide。
 * 适用于非 DTD 节点。
 */
export function computeAppendBalanceAdjustment(
    fillSide: DrCrEntry,
    originalEntry: DrCrEntry,
    diffAmount: number,
): number {
    const absDiff = Math.abs(Number(diffAmount) || 0);
    if (absDiff < 0.001) return 0;
    if (fillSide === 'Dr') {
        return originalEntry === 'Dr' ? absDiff : -absDiff;
    }
    return originalEntry === 'Dr' ? -absDiff : absDiff;
}

/** Append：按目标节点类型选择 balanceAmount 算法（DTD / 非 DTD） */
export function computeAppendBalanceAdjustmentForCode(
    code: string,
    fillSide: DrCrEntry,
    originalEntry: DrCrEntry,
    diffAmount: number,
): number {
    if (isDtdClFsCode(code)) {
        return computeAppendBalanceAdjustmentForDtd(fillSide, diffAmount);
    }
    return computeAppendBalanceAdjustment(fillSide, originalEntry, diffAmount);
}

/** Append：按目标节点类型解析 effective entry（DTD 初始化规则 / 常规 FsCategory） */
export function resolveAppendEffectiveEntry(
    code: string,
    balanceAmount: number,
    originalEntryOverride?: DrCrEntry | string | null,
): DrCrEntry {
    if (isDtdClFsCode(code)) {
        const originalEntry = coerceDrCrEntry(originalEntryOverride)
            ?? getOriginalEntryFromFsCategory(code);
        return resolveDtdAppendEffectiveEntry(balanceAmount, originalEntry);
    }
    const originalEntry = coerceDrCrEntry(originalEntryOverride) ?? getOriginalEntryFromFsCategory(code);
    return determineRenderPosition(originalEntry, balanceAmount);
}

/**
 * 将 AdjustDrCrView 用户输入（可含前导负号触发列切换）转为行级 adjustment。
 * 前导负号导致列切换且绝对值不变时，返回原 adjustment 的反数。
 */
export function computeLineAdjustmentFromUserInput(
    code: string,
    renderPosition: 'Dr' | 'Cr',
    userVal: number,
    previousAdjustment?: number,
    originalEntryOverride?: DrCrEntry | string | null,
    specialDtdPathCodes?: ReadonlySet<string>,
): number {
    const absVal = Math.abs(userVal);
    if (absVal < 0.001) return 0;

    if (usesDtdAdjustmentPathScoped(code, specialDtdPathCodes)) {
        const dtdOrig = coerceDrCrEntry(originalEntryOverride)
            ?? getOriginalEntryFromParent(code);
        const stored = convertDtdClUserInput(renderPosition === 'Dr', userVal, dtdOrig);
        if (previousAdjustment !== undefined && Math.abs(previousAdjustment) >= 0.001) {
            const prevAbs = Math.abs(previousAdjustment);
            if (Math.abs(absVal - prevAbs) < 0.001 && Math.sign(stored) !== Math.sign(previousAdjustment)) {
                return -previousAdjustment;
            }
        }
        return stored;
    }

    const originalEntry = coerceDrCrEntry(originalEntryOverride) ?? getOriginalEntryFromFsCategory(code);
    return computeStoredAdjustmentFromUserInput(
        originalEntry,
        renderPosition,
        userVal,
        previousAdjustment,
    );
}

export function isDrOriginalEntry(entry?: string | null): boolean {
    return entry?.toUpperCase?.().startsWith('D') ?? false;
}

/**
 * 解析 FS 节点 OriginalEntry，与 FsSelectionDrawer / handleFsSelection 逻辑一致：
 * 优先 node.entry，其次 fsAdjustStore.getLineByCode(code).entry。
 * 不读取后端 adjustment line.entry，避免与有效借贷方向混淆。
 */
export function resolveFsNodeOriginalEntry(
    code: string,
    nodeEntry?: string | null,
): DrCrEntry | null {
    if (nodeEntry?.toUpperCase?.().startsWith('D')) return 'Dr';
    if (nodeEntry?.toUpperCase?.().startsWith('C')) return 'Cr';
    const normalized = String(code || '').trim().toUpperCase();
    if (!normalized) return null;
    const line = useFsAdjustStore.getState().getLineByCode(normalized);
    const storeEntry = line?.entry;
    if (storeEntry?.toUpperCase?.().startsWith('D')) return 'Dr';
    if (storeEntry?.toUpperCase?.().startsWith('C')) return 'Cr';
    return null;
}

/**
 * 从 fsAdjustStore 读取 FS 节点 OriginalEntry（Dr/Cr）。
 */
export function getOriginalEntryFromStore(code: string, fallback: DrCrEntry = 'Dr'): DrCrEntry {
    const normalized = String(code || '').trim().toUpperCase();
    if (!normalized) return fallback;
    const line = useFsAdjustStore.getState().getLineByCode(normalized);
    const entry = line?.entry;
    if (entry?.toUpperCase?.().startsWith('C')) return 'Cr';
    if (entry?.toUpperCase?.().startsWith('D')) return 'Dr';
    return fallback;
}

export interface ToPaperPairingOverrides {
    fromOriginalEntry?: string;
    toOriginalEntry?: string;
    /** TaxWp 等 scoped DTD from code（如 BS-CA-TAXR） */
    specialDtdPathCodes?: ReadonlySet<string>;
}

export type FromPaperLike = {
    code?: string;
    adjustment?: number;
    adjustmentLine?: { adjustmentAmount?: number | null; adjustment?: number | null };
} | null;

/** 从 detail 中提取所有 mpf.adjustment 的总和（PL-AE-SAL / PL-AE-WAG） */
function getMpfAdjustmentTotal(detail: any): number {
    if (!detail || typeof detail !== 'object') return 0;
    let total = 0;
    for (const [, value] of Object.entries(detail)) {
        if (!Array.isArray(value)) continue;
        for (const row of value) {
            if (row && typeof row === 'object' && row.mpf && typeof row.mpf === 'object') {
                total += Number(row.mpf.adjustment) || 0;
            }
        }
    }
    return total;
}

/**
 * 与 toPaper 内联计算、Dr/Cr 视图一致：优先 adjustmentLine，其次 componentDetails 汇总，最后 fromPaper.adjustment。
 * 仅 BS-CL-DTD 使用 DTD 专用带符号 adjustment 解析（detail flip 还原）；
 * BS-CA-DIR 及其他节点走常规汇总路径。
 */
export function resolveEffectiveFromAdjustment(
    fromPaper: FromPaperLike,
    getDetail: (code: string) => any,
    specialDtdPathCodes?: ReadonlySet<string>,
): number {
    if (!fromPaper?.code) return 0;

    if (usesDtdAdjustmentPathScoped(fromPaper.code, specialDtdPathCodes)) {
        const fromDetail = getDetail(fromPaper.code);
        const lineAdj = fromPaper.adjustment
            ?? fromPaper.adjustmentLine?.adjustment
            ?? fromPaper.adjustmentLine?.adjustmentAmount;
        // special 路径统一 specialSignedAdjust：DTD 用 dueFromToDir 汇总；TAXR 等用 generic 汇总
        if (isDtdClFsCode(fromPaper.code)) {
            return specialSignedAdjust(fromDetail, lineAdj, sumDueFromToDirDetailAdjustment);
        }
        return specialSignedAdjust(fromDetail, lineAdj, sumGenericDetailAdjustments);
    }

    if (isDueToDirectorsCode(fromPaper.code)) {
        const fromDetail = getDetail(fromPaper.code);
        if (fromDetail) {
            const fromDetails = sumDueFromToDirDetailAdjustment(fromDetail, fromPaper.code);
            if (Math.abs(fromDetails) >= 0.001) return fromDetails;
        }
        return Number(
            fromPaper.adjustment
            ?? fromPaper.adjustmentLine?.adjustment
            ?? fromPaper.adjustmentLine?.adjustmentAmount
            ?? 0,
        ) || 0;
    }

    if (fromPaper.adjustmentLine?.adjustmentAmount !== undefined
        && fromPaper.adjustmentLine?.adjustmentAmount !== null) {
        return Number(fromPaper.adjustment) || 0;
    }

    const fromDetail = getDetail(fromPaper.code);
    if (fromDetail) {
        let total = calculateTotalAdjustment(fromDetail, fromPaper.code);
        if (fromPaper.code === 'PL-AE-SAL' || fromPaper.code === 'PL-AE-WAG') {
            const mpfTotal = getMpfAdjustmentTotal(fromDetail);
            if (Math.abs(mpfTotal) >= 0) {
                total = mpfTotal;
            }
        }
        // 仅当 PL-AE-MPF details 为 salary/wages/directorSalary 执行态时汇总 mpf.adjustment
        // （mpfSummary 展示态仍走上方 calculateTotalAdjustment）
        if (fromPaper.code === 'PL-AE-MPF') {
            const hasMpfExecShape = ['salary', 'wages', 'directorSalary'].some(
                (k) => Array.isArray((fromDetail as any)?.[k]),
            );
            if (hasMpfExecShape) {
                return getMpfAdjustmentTotal(fromDetail);
            }
        }
        return total;
    }

    return fromPaper.adjustment ?? 0;
}

/**
 * 规则 1.1 / 1.2：根据 from/to OriginalEntry 与用户输入 adjustment 计算 toPaper entry 与 details.adjustment。
 *
 * 1.1 from OriginalEntry=Dr → toPaper.entry=Cr
 *   - to OriginalEntry=Dr → details.adjustment 为 fromPaper.adjustment 反数
 *   - to OriginalEntry=Cr → details.adjustment 与 fromPaper.adjustment 同号
 * 1.2 from OriginalEntry=Cr → toPaper.entry=Dr
 *   - to OriginalEntry=Dr → details.adjustment 与 fromPaper.adjustment 同号
 *   - to OriginalEntry=Cr → details.adjustment 为 fromPaper.adjustment 反数
 */
export function toPaperCommonPairing(
    fromOriginalEntry: string,
    toOriginalEntry: string,
    adjustment: number,
): { entry: DrCrEntry; adjustment: number } {
    const fromOrigIsDr = isDrOriginalEntry(fromOriginalEntry);
    const toOrigIsDr = isDrOriginalEntry(toOriginalEntry);
    const entry: DrCrEntry = fromOrigIsDr ? Number(adjustment) < 0 ? 'Dr' : 'Cr' :
        Number(adjustment) < 0 ? 'Cr' : 'Dr';
    const negate = fromOrigIsDr === toOrigIsDr;
    const pairedAdjustment = negate ? Number(-adjustment) : Number(adjustment);
    return {entry, adjustment: pairedAdjustment};
}

/**
 * BS-EQ-AL flat detail：details.adjustment 符号须与 to originalEntry + paired entry 分列一致
 * （determineRenderPosition(toOrig, adj) === entry），否则提交时会按符号重算成错误 entry。
 * 仅 AL 为 flat（非 CommonWp 发起的 commonRow）时需要。
 */
function alignFlatEquityAlAdjustmentToPairedEntry(
    toOriginalEntry: string,
    paired: { entry: DrCrEntry; adjustment: number },
): { entry: DrCrEntry; adjustment: number } {
    const abs = Math.abs(Number(paired.adjustment) || 0);
    if (abs < 0.001) return paired;
    const toOrigIsDr = isDrOriginalEntry(toOriginalEntry);
    const entryIsDr = paired.entry === 'Dr';
    const sameSide = toOrigIsDr === entryIsDr;
    return {
        entry: paired.entry,
        adjustment: sameSide ? abs : -abs,
    };
}

/**
 * BS-CL-TAXP 作为 toPaper 时：Tax payable b/f 行 adjustment 符号与 entry 对齐
 * （与 fromPaper TAXP / TaxWp 显示约定一致，勿用 FS 负债 Cr 的同侧为正）
 * - entry Dr → adjustment 正
 * - entry Cr → adjustment 负
 */
export function alignTaxPayableBfToPaperAdjustment(
    paired: {entry: DrCrEntry; adjustment: number},
): {entry: DrCrEntry; adjustment: number} {
    const abs = Math.abs(Number(paired.adjustment) || 0);
    if (abs < 0.001) return paired;
    return {
        entry: paired.entry,
        adjustment: paired.entry === 'Dr' ? abs : -abs,
    };
}

/**
 * 根据 fromPaper.code / toPaper.code 从 store 读取 OriginalEntry，计算 toPaper entry 与 details.adjustment。
 * 供 AdjustTabsDialog 组装 toPaper 数据结构时使用。
 *
 * BS-CL-DTD / scoped TAXR 走 {@link toPaperSpecialPairing}；其余节点走 OriginalEntry 通用规则。
 * DTD → BS-EQ-AL（flat）：额外校正 flat equity adjustment 符号。
 * 非 special from → BS-CL-TAXP：Tax payable b/f 按 entry Dr+/Cr- 校正（不改 TAXR / special-from / 其它 to）。
 */
export function resolveToPaperFieldsFromPairing(
    fromPaperCode: string,
    adjustment: number,
    toPaperCode: string,
    overrides?: ToPaperPairingOverrides,
): { entry: DrCrEntry; adjustment: number } {
    const normalizedFrom = String(fromPaperCode || '').trim().toUpperCase();
    const normalizedTo = String(toPaperCode || '').trim().toUpperCase();
    const toOriginalEntry = overrides?.toOriginalEntry
        ?? getOriginalEntryFromStore(toPaperCode, 'Cr');

    if (usesDtdAdjustmentPathScoped(normalizedFrom, overrides?.specialDtdPathCodes)) {
        const fromOriginalEntry = coerceDrCrEntry(overrides?.fromOriginalEntry)
            ?? getOriginalEntryFromParent(normalizedFrom);
        const paired = toPaperSpecialPairing(toOriginalEntry, adjustment, fromOriginalEntry);
        // 仅 BS-EQ-AL flat toPaper：pairing 的符号可能与 flat equity 分列约定不一致
        if (normalizedTo === 'BS-EQ-AL') {
            return alignFlatEquityAlAdjustmentToPairedEntry(toOriginalEntry, paired);
        }
        return paired;
    }

    const fromOriginalEntry = overrides?.fromOriginalEntry
        ?? getOriginalEntryFromStore(fromPaperCode);

    const paired = toPaperCommonPairing(
        fromOriginalEntry,
        toOriginalEntry,
        adjustment,
    );
    // 仅普通 from → TAXP / TAXR：按 Dr-side（entry Dr+/Cr-）对齐 TaxWp 显示约定
    if (isTaxBfSpecialDtdCode(normalizedTo)) {
        return alignTaxPayableBfToPaperAdjustment(paired);
    }
    return paired;
}

/**
 * 根据节点原始 entry 与用户输入 adjustment 确定 fromPaper 的 entry / adjustment / adjustmentAmount。
 *
 * 通用规则与 determineRenderPosition 一致（§3.1）：
 * - 原始 Dr + 正数 → entry Dr；原始 Dr + 负数 → entry Cr
 * - 原始 Cr + 正数 → entry Cr；原始 Cr + 负数 → entry Dr
 *
 * 仅 BS-CL-DTD（DueFromToDirectorsWp DTD code）走 DTD 专用规则：
 * - adjustment >= 0 → entry Dr
 * - adjustment < 0  → entry Cr
 *
 * BS-CA-DIR 及其他 paper-registry 组件均走通用 FsCategory 规则。
 */
export function resolveFromPaperAdjustmentFields(
    originalEntry: string,
    adjustment: number,
    code?: string,
    specialDtdPathCodes?: ReadonlySet<string>,
): {
    entry: 'Dr' | 'Cr';
    adjustment: number;
    adjustmentAmount: number;
} {
    const origEntry = usesDtdAdjustmentPathScoped(code, specialDtdPathCodes)
        ? (coerceDrCrEntry(originalEntry) ?? getOriginalEntryFromParent(String(code || '')))
        : coerceDrCrEntry(originalEntry) ?? DTD_DEFAULT_ORIGINAL_ENTRY;
    const entry: 'Dr' | 'Cr' = usesDtdAdjustmentPathScoped(code, specialDtdPathCodes)
        ? resolveDtdEntryFromUserSignedAdjustment(adjustment, origEntry)
        : determineRenderPosition(originalEntry, adjustment);

    return {
        entry,
        adjustment,
        adjustmentAmount: Math.abs(adjustment),
    };
}

/**
 * MPF Adj 列发起 adjustment 时，根据输入符号确定 entry、adjustment 与 adjustmentAmount。
 */
export function resolveMpfAdjustmentEntry(mpfAdjTotal: number): {
    entry: 'Dr' | 'Cr';
    adjustment: number;
    adjustmentAmount: number;
} {
    return resolveFromPaperAdjustmentFields('Dr', mpfAdjTotal);
}

/**
 * 从 categoryLine / payloadLine / fsAdjustStore 解析有效的 fsId。
 */
export function resolveLineFsId(
    code: string,
    categoryLine?: LineFsIdSource,
    payloadLine?: LineFsIdSource,
): number {
    const tryId = (raw: unknown): number => {
        const n = Number(raw);
        return n > 0 ? n : 0;
    };

    let id = tryId((categoryLine as any)?.fsId);
    if (id > 0) return id;

    id = tryId((payloadLine as any)?.fsId);
    if (id > 0) return id;

    const normalized = String(code || '').trim().toUpperCase();
    if (!normalized) return 0;

    const store = useFsAdjustStore.getState();
    id = tryId(store.getLineByCode(normalized)?.fsId);
    return id;
}

/**
 * 调整模式专用：合并数据用于 WP_TO_WP 的 fromPaper 组装
 * 返回包含 details 的完整 line 结构（带负增长 ID）
 *
 * @param params - 统一合并数据所需的参数
 * @returns 返回 Wp2WpResult 结构，只包含 line
 */
export function mergeDataForAdjust(params: UnifiedMergeDataParams): Wp2WpResult | null {
    const {
        categoryLine,
        payloadLine,
        showSnackbar,
        currency,
        amountDifference,
        detailsData,
        negativeIdFields,
        entry,
    } = params;

    const effectiveCategoryLine = categoryLine || payloadLine || null;

    const validation = validateAndGetStrategy(effectiveCategoryLine, showSnackbar);
    if (!validation) {
        return null;
    }

    const {code} = validation;

    // 过滤行数组中的完全空项（保留所有有意义数据的行，包括 perClient=0 或 adjustment=0）
    for (const key of Object.keys(detailsData)) {
        const value = detailsData[key];
        if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
            const beforeCount = value.length;
            detailsData[key] = value.filter((row: any) => {
                // 保留有 item 名称的行
                if (row.item && String(row.item).trim() !== '') return true;
                // 保留有 perClient 值的行（包括 0）
                if (row.perClient !== undefined && row.perClient !== null && row.perClient !== '') return true;
                // 保留有 adjustment 值的行（包括 0）
                if (row.adjustment !== undefined && row.adjustment !== null && row.adjustment !== '') return true;
                // 保留有 lastYearFinal 值的行（包括 0）
                if (row.lastYearFinal !== undefined && row.lastYearFinal !== null && row.lastYearFinal !== '') return true;
                // 保留有 bankName 的行（Bank 类型）
                if (row.bankName && String(row.bankName).trim() !== '') return true;
                // 保留有 bank / loanNo 的行（BankLoan 保存结构）
                if (row.bank && String(row.bank).trim() !== '') return true;
                if (row.loanNo && String(row.loanNo).trim() !== '') return true;
                // 保留有 mv / agreementNo 的行（VehicleLoan 保存结构）
                if (row.mv && String(row.mv).trim() !== '') return true;
                if (row.agreementNo && String(row.agreementNo).trim() !== '') return true;
                // 保留有 directorName 的行（Director 类型）
                if (row.directorName && String(row.directorName).trim() !== '') return true;
                // 保留有 id 的行（后端已保存的数据）
                if (row.id && row.id > 0) return true;
                return false;
            });
        }
    }

    // 为所有行数组生成负增长 ID（WP_TO_WP 需要）
    const fieldsToProcess = negativeIdFields || DEFAULT_NEGATIVE_ID_FIELDS;
    const detailsWithNegativeIds = generateNegativeIdsForAllRows(detailsData, fieldsToProcess);

    // 移除 adjustments/mpfAdjustments 字段（仅用于渲染，不发送到后端）
    const cleanedDetails = removeAdjustmentsFromRows(detailsWithNegativeIds, fieldsToProcess);

    const originalEntry = entry ?? effectiveCategoryLine?.entry ?? 'Dr';
    const resolvedFields = resolveFromPaperAdjustmentFields(originalEntry, amountDifference, code);

    // BS-EQ-AL / BS-CL-RND / BS-EQ-DIV：仅单行 commonRow 时把 line 级带符号 adjustment 同步到该行。
    // WHY: 多行时禁止写到 commonRow[0]——用户在新增行输入 adj 会被污染到首行（如 BS-CL-RND）。
    let detailsForLine = cleanedDetails;
    if (
        isCommonRowSyntheticEditCode(code)
        && detailsForLine
        && typeof detailsForLine === 'object'
        && Array.isArray(detailsForLine.commonRow)
        && detailsForLine.commonRow.length === 1
    ) {
        const only = detailsForLine.commonRow[0];
        detailsForLine = {
            ...detailsForLine,
            commonRow: [{...only, adjustment: resolvedFields.adjustment}],
        };
    }

    // 构建 line 结构，details 使用清理后的数据
    const line: Wp2WpResult['line'] = {
        fsId: resolveLineFsId(code, effectiveCategoryLine, payloadLine),
        code,
        currency,
        adjustment: resolvedFields.adjustment,
        adjustmentAmount: resolvedFields.adjustmentAmount,
        entry: resolvedFields.entry,
        details: [detailsForLine], // WP_TO_WP 使用清理后的数据（移除 adjustments/mpfAdjustments）
    };

    return {line};
}


/**
 * 统一合并和构建调整数据（向后兼容方法）
 * 支持所有 Working Paper 类型，通过 detailsData 透传 strategies-helper 支持的所有字段
 *
 * @param params - 统一合并数据所需的参数
 * @returns 返回 UnifiedMergeDataResult 结构，line 和 details 分离
 */
export function unifiedMergeData(params: UnifiedMergeDataParams): UnifiedMergeDataResult | null {
    const {
        categoryLine,
        payloadLine,
        showSnackbar,
        statement,
        companyId,
        annualId,
        currency,
        entry,
        totalsFinal,
        amountDifference,
        detailsData,
        negativeIdFields,
    } = params;

    // Use line from payload if categoryLine is not available
    const effectiveCategoryLine = categoryLine || payloadLine || null;

    const validation = validateAndGetStrategy(effectiveCategoryLine, showSnackbar);
    if (!validation) {
        return null;
    }

    const {code} = validation;

    // 过滤行数组中的完全空项（保留所有有意义数据的行，包括 perClient=0 或 adjustment=0）
    for (const key of Object.keys(detailsData)) {
        const value = detailsData[key];
        if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
            detailsData[key] = value.filter((row: any) => {
                if (row.item && String(row.item).trim() !== '') return true;
                if (row.perClient !== undefined && row.perClient !== null && row.perClient !== '') return true;
                if (row.adjustment !== undefined && row.adjustment !== null && row.adjustment !== '') return true;
                if (row.lastYearFinal !== undefined && row.lastYearFinal !== null && row.lastYearFinal !== '') return true;
                if (row.bankName && String(row.bankName).trim() !== '') return true;
                if (row.directorName && String(row.directorName).trim() !== '') return true;
                if (row.id && row.id > 0) return true;
                return false;
            });
        }
    }

    // 利用 strategies-helper 构建 detailsPayload（用于后端 API）
    const detailsPayload = buildDetailsPayload({
        code,
        categoryLine: effectiveCategoryLine,
        statement,
        scheduleName: effectiveCategoryLine?.scheduleName,
        companyId: Number(companyId),
        annualId,
        totalFinal: totalsFinal,
        // ...filteredDetailsData, // 透传所有数据字段
        ...detailsData, // 透传所有数据字段
    });

    // 为所有行数组生成负增长 ID
    const fieldsToProcess = negativeIdFields || DEFAULT_NEGATIVE_ID_FIELDS;
    const detailsWithNegativeIds = generateNegativeIdsForAllRows(detailsData, fieldsToProcess);

    const originalEntry = entry ?? effectiveCategoryLine?.entry ?? 'Dr';
    const resolvedFields = resolveFromPaperAdjustmentFields(originalEntry, amountDifference, code);

    // 构建 line 结构，与 AuditAdjustLine 一致（不包含 details）
    const line: UnifiedMergeDataResult['line'] = {
        fsId: effectiveCategoryLine?.fsId || 0,
        code,
        currency,
        adjustment: resolvedFields.adjustment,
        adjustmentAmount: resolvedFields.adjustmentAmount,
        details: [detailsPayload], // 后端 API 需要的 details 格式
        statement,
        scheduleName: effectiveCategoryLine?.scheduleName || '',
        openingAmount: (totalsFinal || 0) - amountDifference,
        closingAmount: totalsFinal || 0,
        entry: resolvedFields.entry,
    };

    // 构建 adjustmentLine，用于向后兼容 FromPaperData（details 使用负增长 ID）
    const adjustmentLine: UnifiedMergeDataResult['adjustmentLine'] = {
        ...line,
        details: [detailsWithNegativeIds], // 覆盖 details，使用负增长 ID（包装为数组）
    };

    // 返回分离的 line 和 details 结构，以及向后兼容的 adjustmentLine
    return {
        companyId: companyId || 0,
        annualId,
        code,
        adjustment: resolvedFields.adjustment,
        entry: resolvedFields.entry,
        line,
        details: detailsWithNegativeIds,
        adjustmentLine,
    };
}

/**
 * PL-COT-PUR 发起调整时，自动追加 PL-COT-COS 伴生 line（与 WP_TO_WP buildSubmitHeader 规则一致）。
 * - 与 PL-COT-PUR 相同 entry
 * - adjustmentAmount 与 PUR 一致（若 lines 中含 BS-CL-OP 则使用其金额）
 * - details 强制为空
 */
export function buildPlCotCosCompanionLine(
    purEntry: string,
    purAmount: number,
    existingLines: Array<{ code?: string; adjustmentAmount?: number }> = [],
): AuditAdjustLine | null {
    const opLine = existingLines.find(
        (l) => String(l.code || '').trim().toUpperCase() === 'BS-CL-OP',
    );
    const cosAmount = opLine != null
        ? Math.abs(Number(opLine.adjustmentAmount) || 0)
        : Math.abs(Number(purAmount) || 0);

    const fsAdjustState = useFsAdjustStore.getState();
    const cosFsLine = fsAdjustState.getLineByCode('PL-COT-COS');
    if (!cosFsLine) {
        return null;
    }

    return {
        fsId: cosFsLine.fsId || 0,
        code: 'PL-COT-COS',
        entry: purEntry,
        adjustmentAmount: cosAmount,
        currency: 'HKD',
        details: [],
    };
}

